import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Platform,
  type StyleProp,
  type ViewStyle,
} from 'react-native';

import { formFieldLabelText } from './formFieldLabelStyle';
import { colors } from '../../theme/colors';
import { spacing } from '../../theme/spacing';
import { fontSize, lineHeight, fontWeight } from '../../theme/typography';
import { AlertCircle } from '../icons';

/** Em dash — matches rackListUtils / attachmentFormatters empty display. */
export const EMPTY_FIELD_DISPLAY = '—';

/**
 * Shared empty check for view-only field presentation (#1325).
 * Empty → dash. `0`, `false`, and `"0"` are not empty.
 */
export function isEmptyFieldValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  if (typeof value === 'boolean' || typeof value === 'number') {
    return false;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

export function resolveViewOnlyDisplayText(value: unknown): string {
  if (isEmptyFieldValue(value)) {
    return EMPTY_FIELD_DISPLAY;
  }
  if (Array.isArray(value)) {
    return value.map(String).join(', ');
  }
  return String(value);
}

/** Yes/No controls: only explicit `Yes` or `No` counts as populated. */
export function resolveYesNoViewOnlyDisplayText(value: unknown): string {
  if (value === 'Yes' || value === 'No') {
    return value;
  }
  return EMPTY_FIELD_DISPLAY;
}

/** Checkbox: boolean maps to Yes/No; never empty when boolean. */
export function resolveCheckboxViewOnlyDisplayText(value: boolean): string {
  return value ? 'Yes' : 'No';
}

export type ViewOnlyPresentationParams = {
  readOnlyMode?: boolean;
  disabled?: boolean;
  editable?: boolean;
  canEditRequest?: boolean;
  ignorePermissions?: boolean;
};

/** Request-detail view vs edit field props (#1325). */
export function requestStepFieldModeProps(isEditing: boolean): {
  disabled: boolean;
  readOnlyMode: boolean;
} {
  if (isEditing) {
    return { disabled: false, readOnlyMode: false };
  }
  return { disabled: true, readOnlyMode: true };
}

/**
 * Shared view-only gate for form primitives (#1325).
 * Request view mode (`disabled` alone) → plain text / dash.
 * Permission-locked or legacy read-only chrome (`editable={false}`) → not view-only.
 */
export function resolveIsViewOnlyPresentation({
  readOnlyMode = false,
  disabled = false,
  editable = true,
  canEditRequest = true,
  ignorePermissions = false,
}: ViewOnlyPresentationParams): boolean {
  const isEditable = ignorePermissions ? editable : editable && canEditRequest;
  const isPermissionLocked = !disabled && !readOnlyMode && !isEditable;
  const isDisabled = disabled || readOnlyMode || isPermissionLocked;
  const isReadOnlyInput =
    isDisabled && (readOnlyMode || editable === false || isPermissionLocked);
  return readOnlyMode || (disabled && !isReadOnlyInput && !isPermissionLocked);
}

export type ViewOnlyFieldValueProps = {
  label: string;
  value: unknown;
  /** When set, overrides default empty/populated resolution. */
  displayText?: string;
  hideLabel?: boolean;
  multiline?: boolean;
  testID?: string;
  valueTestID?: string;
  containerStyle?: StyleProp<ViewStyle>;
  error?: string;
  helperText?: string;
  /** Sits beside the value. Used by the unit chip in view mode. */
  trailing?: React.ReactNode;
};

/** Shared label + plain-text presenter for request view mode (#1325). */
export const ViewOnlyFieldValue: React.FC<ViewOnlyFieldValueProps> = ({
  label,
  value,
  displayText,
  hideLabel = false,
  multiline = false,
  testID,
  valueTestID = 'view-only-field-value',
  containerStyle,
  error,
  helperText,
  trailing,
}) => {
  const text =
    displayText != null ? displayText : resolveViewOnlyDisplayText(value);
  const empty =
    displayText != null
      ? text === EMPTY_FIELD_DISPLAY
      : isEmptyFieldValue(value);
  const a11yLabel = empty ? `${label}, empty` : `${label}, ${text}`;

  return (
    <View
      style={[
        styles.formGroup,
        hideLabel && styles.formGroupValueOnlyRowBreak,
        containerStyle,
      ]}
      testID={testID}
    >
      {!hideLabel ? (
        <View style={styles.labelContainer}>
          <Text style={styles.label} accessible={false}>
            {label}
          </Text>
        </View>
      ) : null}
      <View style={trailing ? styles.valueWithTrailing : undefined}>
        <Text
          style={[
            styles.value,
            empty ? styles.valueEmpty : styles.valuePopulated,
            multiline && styles.valueMultiline,
            trailing ? styles.valueBesideTrailing : null,
          ]}
          accessibilityRole="text"
          accessibilityLabel={a11yLabel}
          accessibilityHint={empty ? 'No value provided' : undefined}
          focusable={false}
          testID={valueTestID}
          {...Platform.select({
            web: {
              'aria-label': a11yLabel,
            },
          })}
        >
          {text}
        </Text>
        {trailing}
      </View>
      {error ? (
        <View
          style={styles.errorRow}
          accessibilityRole="text"
          accessibilityLabel={error}
        >
          <AlertCircle
            size={14}
            color={colors.error}
            style={styles.errorRowIcon}
          />
          <Text
            style={styles.errorText}
            {...Platform.select({
              web: { id: `${label}-error` },
            })}
          >
            {error}
          </Text>
        </View>
      ) : null}
      {helperText && !error ? (
        <Text
          style={styles.helperText}
          {...Platform.select({
            web: { id: `${label}-helper` },
          })}
        >
          {helperText}
        </Text>
      ) : null}
    </View>
  );
};

ViewOnlyFieldValue.displayName = 'ViewOnlyFieldValue';

const styles = StyleSheet.create({
  formGroup: {
    marginBottom: 0,
    ...Platform.select({
      web: {
        minWidth: 0,
        maxWidth: '100%' as const,
        alignSelf: 'stretch' as const,
      },
      default: {},
    }),
  },
  /**
   * When the label is rendered by a sibling (e.g. `FormFieldLabelWithInfo` + `hideLabel`),
   * force the value onto the next line in flex-row field rows (#1325 consistency).
   */
  formGroupValueOnlyRowBreak: {
    width: '100%' as const,
    flexBasis: '100%' as const,
    flexGrow: 1,
    flexShrink: 0,
    alignSelf: 'stretch' as const,
  },
  labelContainer: {
    width: '100%' as const,
    marginBottom: spacing.sm,
  },
  label: {
    ...formFieldLabelText,
  },
  valueWithTrailing: {
    flexDirection: 'row',
    alignItems: 'center',
    width: '100%' as const,
  },
  valueBesideTrailing: {
    width: undefined,
    maxWidth: undefined,
    flex: 1,
  },
  value: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    fontWeight: fontWeight.regular,
    marginTop: spacing.xs,
    width: '100%' as const,
    flexShrink: 1,
    alignSelf: 'stretch' as const,
    minWidth: 0,
    maxWidth: '100%' as const,
  },
  valueEmpty: {
    color: colors.textMuted,
  },
  valuePopulated: {
    color: colors.textSecondary,
  },
  valueMultiline: {
    textAlignVertical: 'top',
  },
  errorRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    marginTop: spacing.xs,
    gap: spacing.formInlineFeedbackGap,
  },
  errorRowIcon: {
    marginTop: 1,
  },
  errorText: {
    fontSize: fontSize.charCount,
    color: colors.error,
    flex: 1,
  },
  helperText: {
    fontSize: fontSize.charCount,
    color: colors.textSecondary,
    marginTop: spacing.xs,
  },
});
